import { createFileRoute, notFound } from "@tanstack/react-router";
import { z } from "zod";
import { ProfilePage } from "@/features/profile/ProfilePage";
import { GET_STARTED, workspaceNav } from "@/features/shell/workspaceNav";
import { WorkspaceAuditPage } from "@/features/workspace/AuditPage";
import { WorkspaceBillingPage } from "@/features/workspace/BillingPage";
import { WorkspaceChannelsPage } from "@/features/workspace/ChannelsPage";
import { WorkspaceConnectionsPage } from "@/features/workspace/ConnectionsPage";
import { GetStartedPage } from "@/features/workspace/GetStartedPage";
import { type MembersTab, WorkspaceMembersPage } from "@/features/workspace/MembersPage";
import { WorkspaceProjectsPage } from "@/features/workspace/ProjectsPage";
import { WorkspaceSettingsPage } from "@/features/workspace/WorkspaceSettingsPage";

const search = z.object({
  governance: z.string().optional(),
  add: z.boolean().optional(),
  connection: z.string().optional(),
  tab: z.enum(["people", "groups", "service", "requests", "roles"]).optional(),
  invite: z.boolean().optional(),
  actor: z.string().optional(),
  /** Set by the GitHub App install redirect landing back on Get started. */
  github: z.enum(["connected", "expired", "taken", "unverified", "none", "several"]).optional(),
});

export const Route = createFileRoute("/w/$workspace/_ws/$section")({
  validateSearch: search,
  beforeLoad: ({ params }) => {
    if (params.section !== "profile" && !workspaceNav.some((n) => n.id === params.section))
      throw notFound();
  },
  component: WorkspaceSection,
});

function WorkspaceSection() {
  const { workspace, section } = Route.useParams();
  const s = Route.useSearch();
  const navigate = Route.useNavigate();
  const set = (patch: Partial<z.infer<typeof search>>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  switch (section) {
    case GET_STARTED:
      return (
        <GetStartedPage
          workspace={workspace}
          githubParam={s.github}
          onGithubParamHandled={() => set({ github: undefined })}
        />
      );
    case "projects":
      return (
        <WorkspaceProjectsPage
          workspace={workspace}
          governance={s.governance ?? null}
          onGovernance={(id) => set({ governance: id ?? undefined })}
        />
      );
    case "connections":
      return (
        <WorkspaceConnectionsPage
          workspace={workspace}
          adding={s.add ?? false}
          onAdding={(v) => set({ add: v || undefined })}
          selected={s.connection ?? null}
          onSelect={(id: string | null) => set({ connection: id ?? undefined })}
        />
      );
    case "members":
      return (
        <WorkspaceMembersPage
          workspace={workspace}
          tab={(s.tab as MembersTab) ?? "people"}
          onTab={(t) => set({ tab: t === "people" ? undefined : t })}
          inviting={s.invite ?? false}
          onInviting={(v) => set({ invite: v || undefined })}
        />
      );
    case "channels":
      return (
        <WorkspaceChannelsPage
          workspace={workspace}
          adding={s.add ?? false}
          onAdding={(v) => set({ add: v || undefined })}
        />
      );
    case "billing":
      return <WorkspaceBillingPage workspace={workspace} />;
    case "profile":
      return <ProfilePage workspace={workspace} />;
    case "audit":
      return <WorkspaceAuditPage workspace={workspace} actor={s.actor} />;
    default:
      return <WorkspaceSettingsPage workspace={workspace} />;
  }
}
